import { Input, InputNumber, Typography } from "antd";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { repeaterFormSchema, type RepeaterFormData } from "components/CC/Nodes/RepeaterNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect } from "react";
import { FormItem } from "react-hook-form-antd";

interface FormProps {
    onChange?: (data: RepeaterFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
}

export const RepeaterForm = ({ onChange, onRegisterValidator, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<RepeaterFormData>
            key={selectedNode?.id}
            id={formId}
            schema={repeaterFormSchema}
            defaultValues={selectedNode?.formData as RepeaterFormData}
        >
            <FormContent onChange={onChange} onRegisterValidator={onRegisterValidator} />
        </GeneralFormProvider>
    );
};

const FormContent = ({
    onChange,
    onRegisterValidator,
}: {
    onChange?: (data: RepeaterFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
}) => {
    const { control, trigger } = useGeneralForm<RepeaterFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<RepeaterFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.repeater.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.repeater.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="title"
                    label={t("nodes.repeater.forms.title")}
                >
                    <Input placeholder={t("nodes.repeater.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.repeater.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.repeater.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Maximum repetition */}
            <FormItem
                className="!mb-0"
                control={control}
                name="maxRepetition"
                label={t("nodes.repeater.forms.max_repetition")}
            >
                <InputNumber className="w-full" min={1} />
            </FormItem>
        </div>
    );
};
